import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Users, FileText, FilePlus, Clock, ChevronRight, LogOut, Settings } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Dashboard() {
    const auth = useAuth();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(function() {
        axios.get(API_URL + '/api/dashboard/stats').then(function(res) {
            setStats(res.data);
        }).catch(function() {
            toast.error('Failed to load dashboard');
        }).finally(function() {
            setLoading(false);
        });
    }, []);

    function fmtDate(d) { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    
    // Format middle name/initial - add period if single letter
    function fmtMiddle(m) { return m && m.trim().length === 1 ? m.trim() + '.' : m; }
    function fmtFullName(first, middle, last) { return [first, fmtMiddle(middle), last].filter(Boolean).join(' '); }

    const el = React.createElement;

    const header = el('header', { className: 'sticky top-0 z-50 border-b border-stone-200 bg-white/80 backdrop-blur-sm' },
        el('div', { className: 'flex items-center justify-between px-6 py-4 max-w-7xl mx-auto' },
            el(Link, { to: '/dashboard', className: 'flex items-center gap-3' },
                el('img', { src: '/lion-logo.png', alt: 'BestWillsAndTrusts', className: 'h-10 w-10 object-contain' }),
                el('span', { className: 'font-serif text-xl font-semibold hidden sm:block text-amber-600' }, 'BestWillsAndTrusts')
            ),
            el('nav', { className: 'hidden md:flex items-center gap-8' },
                el(Link, { to: '/dashboard', className: 'text-sm font-medium text-amber-600' }, 'Dashboard'),
                el(Link, { to: '/clients', className: 'text-sm font-medium text-muted-foreground hover:text-foreground' }, 'Clients'),
                el(Link, { to: '/documents', className: 'text-sm font-medium text-muted-foreground hover:text-foreground' }, 'Documents'),
                el(Link, { to: '/templates', className: 'text-sm font-medium text-muted-foreground hover:text-foreground' }, 'Templates'),
                auth.user?.role === 'admin' && el(Link, { to: '/admin/users', className: 'text-sm font-medium text-muted-foreground hover:text-foreground' }, 'Users'),
                auth.user?.role === 'admin' && el(Link, { to: '/admin/security-logs', className: 'text-sm font-medium text-muted-foreground hover:text-foreground' }, 'Security'),
                auth.user?.role === 'admin' && el(Link, { to: '/admin/activity-logs', className: 'text-sm font-medium text-muted-foreground hover:text-foreground' }, 'Activity')
            ),
            el('div', { className: 'flex items-center gap-4' },
                el('span', { className: 'text-sm text-muted-foreground hidden sm:block' }, auth.user?.name),
                el(Link, { to: '/settings' },
                    el(Button, { variant: 'ghost', size: 'sm', className: 'rounded-none' },
                        el(Settings, { className: 'h-4 w-4' })
                    )
                ),
                el(Button, { variant: 'ghost', size: 'sm', onClick: function(e) { e.preventDefault(); auth.logout(); }, className: 'rounded-none cursor-pointer', 'data-testid': 'logout-btn' },
                    el(LogOut, { className: 'h-4 w-4 mr-2' }), 'Sign Out'
                )
            )
        )
    );

    const welcome = el('div', { className: 'mb-8' },
        el('h1', { className: 'font-serif text-3xl lg:text-4xl mb-2' }, 'Welcome back, ' + (auth.user?.name?.split(' ')[0] || '')),
        el('p', { className: 'text-muted-foreground' }, "Here's an overview of your estate planning practice")
    );

    const actions = el('div', { className: 'flex flex-wrap gap-4 mb-8' },
        el(Link, { to: '/clients/new' },
            el(Button, { className: 'rounded-none font-medium uppercase tracking-wide', 'data-testid': 'new-client-btn' },
                el(FilePlus, { className: 'h-4 w-4 mr-2' }), 'New Client Intake'
            )
        ),
        el(Link, { to: '/clients' },
            el(Button, { variant: 'outline', className: 'rounded-none', 'data-testid': 'view-clients-btn' },
                el(Users, { className: 'h-4 w-4 mr-2' }), 'View All Clients'
            )
        )
    );

    const statsGrid = loading
        ? el('div', { className: 'grid grid-cols-1 md:grid-cols-2 gap-6' },
            el('div', { className: 'h-32 bg-stone-100 animate-pulse' }),
            el('div', { className: 'h-32 bg-stone-100 animate-pulse' })
          )
        : el('div', { className: 'grid grid-cols-1 md:grid-cols-2 gap-6 mb-8' },
            el(Card, { className: 'rounded-none border-stone-200' },
                el(CardHeader, { className: 'pb-2' }, el(CardDescription, { className: 'text-xs uppercase tracking-wide' }, 'Total Clients')),
                el(CardContent, null,
                    el('div', { className: 'flex items-end justify-between' },
                        el('div', null,
                            el('p', { className: 'font-serif text-5xl font-semibold text-primary' }, stats?.total_clients || 0),
                            el('p', { className: 'text-sm text-muted-foreground mt-2' }, 'Active intake records')
                        ),
                        el(Users, { className: 'h-12 w-12 text-primary/20' })
                    )
                )
            ),
            el(Card, { className: 'rounded-none border-stone-200' },
                el(CardHeader, { className: 'pb-2' }, el(CardDescription, { className: 'text-xs uppercase tracking-wide' }, 'Documents Generated')),
                el(CardContent, null,
                    el('div', { className: 'flex items-end justify-between' },
                        el('div', null,
                            el('p', { className: 'font-serif text-5xl font-semibold text-primary' }, stats?.total_documents || 0),
                            el('p', { className: 'text-sm text-muted-foreground mt-2' }, 'Legal documents created')
                        ),
                        el(FileText, { className: 'h-12 w-12 text-primary/20' })
                    )
                )
            )
          );

    const recentClientsContent = stats?.recent_clients?.length > 0
        ? el('div', { className: 'space-y-4' },
            stats.recent_clients.map(function(c) {
                return el(Link, { key: c.id, to: '/clients/' + c.id, className: 'flex items-center justify-between p-3 border border-stone-200 hover:border-primary/30 hover:bg-stone-50', 'data-testid': 'recent-client-' + c.id },
                    el('div', null,
                        el('p', { className: 'font-medium' }, fmtFullName(c.first_name, c.middle_name, c.last_name)),
                        el('p', { className: 'text-sm text-muted-foreground' }, c.email || 'No email')
                    ),
                    el('div', { className: 'flex items-center gap-2 text-sm text-muted-foreground' },
                        el(Clock, { className: 'h-4 w-4' }),
                        fmtDate(c.created_at)
                    )
                );
            })
          )
        : el('div', { className: 'text-center py-8 text-muted-foreground' },
            el(Users, { className: 'h-12 w-12 mx-auto mb-4 opacity-50' }),
            el('p', null, 'No clients yet'),
            el(Link, { to: '/clients/new' },
                el(Button, { variant: 'link', className: 'mt-2' }, 'Add your first client')
            )
          );

    const recentDocsContent = stats?.recent_documents?.length > 0
        ? el('div', { className: 'space-y-4' },
            stats.recent_documents.map(function(d) {
                return el(Link, { key: d.id, to: '/documents/' + d.id, className: 'flex items-center justify-between p-3 border border-stone-200 hover:border-primary/30 hover:bg-stone-50', 'data-testid': 'recent-doc-' + d.id },
                    el('div', { className: 'flex items-center gap-3' },
                        el('div', { className: 'p-2 bg-primary/10' }, el(FileText, { className: 'h-4 w-4 text-primary' })),
                        el('div', null,
                            el('p', { className: 'font-medium text-sm' }, d.document_type),
                            el('p', { className: 'text-sm text-muted-foreground' }, d.client_name)
                        )
                    ),
                    el('div', { className: 'text-sm text-muted-foreground' }, 'v' + d.version)
                );
            })
          )
        : el('div', { className: 'text-center py-8 text-muted-foreground' },
            el(FileText, { className: 'h-12 w-12 mx-auto mb-4 opacity-50' }),
            el('p', null, 'No documents generated yet'),
            el('p', { className: 'text-sm mt-1' }, 'Add a client to start')
          );

    const recentGrid = el('div', { className: 'grid grid-cols-1 lg:grid-cols-2 gap-6' },
        el(Card, { className: 'rounded-none border-stone-200' },
            el(CardHeader, null,
                el('div', { className: 'flex items-center justify-between' },
                    el('div', null,
                        el(CardTitle, { className: 'font-serif text-xl' }, 'Recent Clients'),
                        el(CardDescription, null, 'Newly added intake records')
                    ),
                    el(Link, { to: '/clients' },
                        el(Button, { variant: 'ghost', size: 'sm', className: 'rounded-none' },
                            'View all ', el(ChevronRight, { className: 'h-4 w-4 ml-1' })
                        )
                    )
                )
            ),
            el(CardContent, null, recentClientsContent)
        ),
        el(Card, { className: 'rounded-none border-stone-200' },
            el(CardHeader, null,
                el('div', { className: 'flex items-center justify-between' },
                    el('div', null,
                        el(CardTitle, { className: 'font-serif text-xl' }, 'Recent Documents'),
                        el(CardDescription, null, 'Latest generated documents')
                    ),
                    el(Link, { to: '/documents' },
                        el(Button, { variant: 'ghost', size: 'sm', className: 'rounded-none' },
                            'View all ', el(ChevronRight, { className: 'h-4 w-4 ml-1' })
                        )
                    )
                )
            ),
            el(CardContent, null, recentDocsContent)
        )
    );

    return el('div', { className: 'min-h-screen bg-[#FDFCF8]' },
        header,
        el('main', { className: 'max-w-7xl mx-auto px-6 py-8' },
            welcome,
            actions,
            statsGrid,
            recentGrid
        )
    );
}

export default Dashboard;
